import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Disc3, Music2,
  ChevronDown, Speaker, CalendarClock, Plus, Trash2, Link2, Unlink, MapPin,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Slider } from "./ui/slider";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import { Switch } from "./ui/switch";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ConnectCTA({ canConnect }) {
  const connect = () => {
    window.location.href = `${BACKEND_URL}/api/sonos/oauth/start?hub=${encodeURIComponent(window.location.origin)}`;
  };
  return (
    <div className="border border-dashed border-brand-borderStrong rounded-xl bg-brand-surface p-10 text-center space-y-4" data-testid="sonos-connect-cta">
      <div className="h-12 w-12 rounded-full bg-brand-ink/5 flex items-center justify-center mx-auto">
        <Music2 className="h-6 w-6 text-brand-ink" />
      </div>
      <div>
        <h3 className="font-serif text-xl text-brand-ink">Connect Sonos</h3>
        <p className="text-sm text-brand-muted mt-1 max-w-md mx-auto">
          Link the Bond Collective Sonos account to control playback, volume and playlists across every location.
        </p>
      </div>
      {canConnect ? (
        <button onClick={connect} data-testid="sonos-connect-btn"
          className="inline-flex items-center gap-2 text-sm rounded-md px-5 py-2.5 bg-brand-ink text-brand-page hover:opacity-90 transition-opacity">
          <Link2 className="h-4 w-4" /> Connect Sonos account
        </button>
      ) : (
        <p className="text-xs text-brand-tertiary">Ask an admin to connect the Sonos account.</p>
      )}
    </div>
  );
}

function UnassignedRooms({ rooms, assignable, onAssign }) {
  if (!rooms?.length) return null;
  return (
    <div className="border border-brand-gold/30 bg-brand-gold/5 rounded-xl p-5" data-testid="sonos-unassigned">
      <h4 className="inline-flex items-center gap-2 text-sm font-medium text-brand-ink mb-1">
        <MapPin className="h-4 w-4 text-brand-gold" /> Rooms needing a location
      </h4>
      <p className="text-xs text-brand-muted mb-4">Assign each Sonos room to a Bond location so it shows on the right dashboard.</p>
      <div className="space-y-2">
        {rooms.map((r) => (
          <div key={r.id} data-testid={`sonos-unassigned-${r.id}`} className="flex items-center gap-3 bg-brand-surface border border-brand-border rounded-lg px-3 py-2.5">
            <Speaker className="h-4 w-4 text-brand-tertiary shrink-0" />
            <span className="text-sm text-brand-ink flex-1 truncate">{r.name}</span>
            <Select onValueChange={(loc) => onAssign(r, loc)}>
              <SelectTrigger className="h-9 w-48 text-xs" data-testid={`sonos-assign-${r.id}`}><SelectValue placeholder="Choose location…" /></SelectTrigger>
              <SelectContent>{assignable.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlaylistCover({ accent = "#1E3A2F", size = "h-14 w-14", icon = true }) {
  return (
    <div className={`${size} shrink-0 rounded-md flex items-center justify-center relative overflow-hidden`}
      style={{ background: `linear-gradient(135deg, ${accent} 0%, #1A1816 120%)` }}>
      <div className="absolute inset-0 opacity-20"
        style={{ backgroundImage: "radial-gradient(circle at 70% 30%, #fff 0, transparent 40%)" }} />
      {icon && <Disc3 className="h-6 w-6 text-white/85" strokeWidth={1.25} />}
    </div>
  );
}

function GroupCard({ group, location, playlists }) {
  const [g, setG] = useState(group);
  const [vol, setVol] = useState(group.volume);
  const [open, setOpen] = useState(false);
  const playing = g.playbackState === "PLAYBACK_STATE_PLAYING";
  const np = g.nowPlaying;

  useEffect(() => { setG(group); setVol(group.volume); }, [group]);

  // Fire-and-forget: update the UI immediately, send one Sonos command, revert on failure.
  const fire = (req, optimistic, prevSnapshot) => {
    setG(optimistic);
    req.catch((e) => {
      setG(prevSnapshot);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sonos command failed");
    });
  };

  const transport = (cmd) => {
    const prev = g;
    const next = cmd === "play" ? { ...g, playbackState: "PLAYBACK_STATE_PLAYING" }
      : cmd === "pause" ? { ...g, playbackState: "PLAYBACK_STATE_PAUSED" } : g;
    fire(api.post(`/sonos/groups/${g.id}/playback/${cmd}`, null, { params: { location } }), next, prev);
  };
  const commitGroupVol = (v) => {
    const prev = g;
    fire(api.post(`/sonos/groups/${g.id}/volume`, { volume: v }, { params: { location } }), { ...g, volume: v }, prev);
  };
  const toggleMute = () => {
    const prev = g;
    fire(api.post(`/sonos/groups/${g.id}/volume`, { volume: g.volume, muted: !g.muted }, { params: { location } }), { ...g, muted: !g.muted }, prev);
  };
  const commitPlayerVol = (pid, v) =>
    api.post(`/sonos/groups/${g.id}/players/${pid}/volume`, { volume: v }, { params: { location } })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't set speaker volume"));
  const choosePlaylist = (pid) => {
    const prev = g;
    const pl = playlists.find((p) => p.id === pid);
    const next = {
      ...g, playbackState: "PLAYBACK_STATE_PLAYING",
      nowPlaying: pl ? { playlistId: pid, playlistName: pl.name, service: pl.service, accent: pl.accent, track: pl.name, artist: pl.service } : g.nowPlaying,
    };
    fire(api.post(`/sonos/groups/${g.id}/playlist`, { playlist_id: pid }, { params: { location } }), next, prev);
  };

  return (
    <div className="bg-brand-surface border border-brand-border rounded-xl p-5 flex flex-col gap-4 transition-all duration-200 hover:border-brand-borderStrong hover:shadow-sm"
      data-testid={`sonos-group-${group.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h4 className="font-serif text-lg text-brand-ink leading-tight truncate">{g.name}</h4>
          <span className="text-[11px] uppercase tracking-[0.14em] text-brand-tertiary inline-flex items-center gap-1.5">
            <Speaker className="h-3 w-3" />{g.players.length} speaker{g.players.length === 1 ? "" : "s"}
          </span>
        </div>
        <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${playing ? "bg-brand-pos/10 text-brand-pos" : "bg-brand-ink/5 text-brand-tertiary"}`}
          data-testid={`sonos-state-${group.id}`}>
          {playing ? "Playing" : g.playbackState === "PLAYBACK_STATE_PAUSED" ? "Paused" : "Idle"}
        </span>
      </div>

      {/* Now playing */}
      <div className="flex items-center gap-3">
        <PlaylistCover accent={np?.accent || "#8A847A"} />
        <div className="min-w-0 flex-1">
          {np ? (
            <>
              <p className="text-sm text-brand-ink font-medium truncate" data-testid={`sonos-track-${group.id}`}>{np.track}</p>
              <p className="text-xs text-brand-muted truncate">{np.artist}</p>
              <p className="text-[10px] uppercase tracking-[0.14em] text-brand-tertiary mt-0.5 truncate">{np.playlistName} · {np.service}</p>
            </>
          ) : (
            <p className="text-sm text-brand-tertiary italic">Nothing playing</p>
          )}
        </div>
      </div>

      {/* Transport */}
      <div className="flex items-center justify-center gap-2">
        <button onClick={() => transport("skipPrevious")} data-testid={`sonos-prev-${group.id}`}
          className="p-2 text-brand-muted hover:text-brand-ink transition-colors"><SkipBack className="h-4 w-4" /></button>
        <button onClick={() => transport(playing ? "pause" : "play")} data-testid={`sonos-playpause-${group.id}`}
          className="h-11 w-11 rounded-full bg-brand-ink text-brand-page flex items-center justify-center hover:opacity-90 transition-opacity">
          {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </button>
        <button onClick={() => transport("skipNext")} data-testid={`sonos-next-${group.id}`}
          className="p-2 text-brand-muted hover:text-brand-ink transition-colors"><SkipForward className="h-4 w-4" /></button>
      </div>

      {/* Group volume */}
      <div className="flex items-center gap-3">
        <button onClick={toggleMute} data-testid={`sonos-mute-${group.id}`} className="text-brand-muted hover:text-brand-ink transition-colors">
          {g.muted ? <VolumeX className="h-4 w-4 text-brand-neg" /> : <Volume2 className="h-4 w-4" />}
        </button>
        <Slider value={[vol]} min={0} max={100} step={1} data-testid={`sonos-group-vol-${group.id}`}
          onValueChange={(v) => setVol(v[0])} onValueCommit={(v) => commitGroupVol(v[0])} className="flex-1" />
        <span className="text-xs font-mono text-brand-muted w-8 text-right tabular-nums">{vol}</span>
      </div>

      {/* Playlist picker */}
      <Select value={np?.playlistId || ""} onValueChange={choosePlaylist}>
        <SelectTrigger className="h-9 text-xs bg-brand-page border-brand-border" data-testid={`sonos-playlist-select-${group.id}`}>
          <span className="inline-flex items-center gap-2 truncate"><Music2 className="h-3.5 w-3.5 text-brand-tertiary" />
            <SelectValue placeholder="Choose a playlist…" /></span>
        </SelectTrigger>
        <SelectContent>
          {playlists.filter((p) => !p.householdId || !g.householdId || p.householdId === g.householdId).map((p) => (
            <SelectItem key={p.id} value={p.id} data-testid={`sonos-playlist-opt-${p.id}`}>{p.name}{p.service ? ` · ${p.service}` : ""}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Per-speaker volumes */}
      <div>
        <button onClick={() => setOpen(!open)} data-testid={`sonos-speakers-toggle-${group.id}`}
          className="inline-flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-ink transition-colors">
          <Speaker className="h-3.5 w-3.5" /> Per-speaker volume
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="mt-3 space-y-3" data-testid={`sonos-speakers-${group.id}`}>
            {g.players.map((pl) => (
              <SpeakerRow key={pl.id} player={pl} onCommit={(v) => commitPlayerVol(pl.id, v)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SpeakerRow({ player, onCommit }) {
  const [v, setV] = useState(player.volume);
  useEffect(() => { setV(player.volume); }, [player.volume]);
  return (
    <div className="flex items-center gap-3" data-testid={`sonos-speaker-${player.id}`}>
      <span className="text-xs text-brand-ink w-24 truncate">{player.name}</span>
      <Slider value={[v]} min={0} max={100} step={1} onValueChange={(x) => setV(x[0])}
        onValueCommit={(x) => onCommit(x[0])} className="flex-1" />
      <span className="text-xs font-mono text-brand-muted w-8 text-right tabular-nums">{v}</span>
    </div>
  );
}

function ScheduleDialog({ location, groups, playlists, onSaved }) {
  const [open, setOpen] = useState(false);
  const [groupId, setGroupId] = useState(groups[0]?.id || "");
  const [playlistId, setPlaylistId] = useState(playlists[0]?.id || "");
  const [days, setDays] = useState([0, 1, 2, 3, 4]);
  const [start, setStart] = useState("08:00");
  const [end, setEnd] = useState("18:00");
  const [vol, setVol] = useState(25);
  const [saving, setSaving] = useState(false);

  const toggleDay = (d) => setDays((cur) => cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort());

  const save = async () => {
    const group = groups.find((g) => g.id === groupId);
    const pl = playlists.find((p) => p.id === playlistId);
    if (!group || !pl || !days.length) { toast.error("Pick a room, a playlist and at least one day"); return; }
    if (end <= start) { toast.error("End time must be after start time"); return; }
    setSaving(true);
    try {
      await api.post("/sonos/schedules", {
        location, group_id: groupId, group_name: group.name,
        playlist_id: playlistId, playlist_name: pl.name,
        days, start_time: start, end_time: end, volume: vol, enabled: true,
      });
      toast.success("Schedule saved");
      setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save schedule"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="sonos-add-schedule" className="inline-flex items-center gap-1.5 text-xs rounded-md px-3 py-1.5 bg-brand-ink text-brand-page hover:opacity-90 transition-opacity">
          <Plus className="h-3.5 w-3.5" /> Add schedule
        </button>
      </DialogTrigger>
      <DialogContent className="bg-brand-surface border-brand-border" data-testid="sonos-schedule-dialog">
        <DialogHeader><DialogTitle className="font-serif text-xl text-brand-ink">New play schedule · {location}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-brand-tertiary block mb-1.5">Room</label>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger className="h-9 text-xs" data-testid="sonos-sched-room"><SelectValue /></SelectTrigger>
                <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-brand-tertiary block mb-1.5">Playlist</label>
              <Select value={playlistId} onValueChange={setPlaylistId}>
                <SelectTrigger className="h-9 text-xs" data-testid="sonos-sched-playlist"><SelectValue /></SelectTrigger>
                <SelectContent>{playlists.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.14em] text-brand-tertiary block mb-1.5">Days</label>
            <div className="flex gap-1.5">
              {DAYS.map((d, i) => (
                <button key={d} onClick={() => toggleDay(i)} data-testid={`sonos-sched-day-${i}`}
                  className={`h-8 w-9 rounded-md text-xs font-medium transition-colors ${days.includes(i) ? "bg-brand-ink text-brand-page" : "bg-brand-page border border-brand-border text-brand-muted hover:border-brand-borderStrong"}`}>
                  {d[0]}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-brand-tertiary block mb-1.5">Start</label>
              <input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="sonos-sched-start"
                className="h-9 w-full rounded-md border border-brand-border bg-brand-page px-3 text-xs text-brand-ink" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-brand-tertiary block mb-1.5">End</label>
              <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="sonos-sched-end"
                className="h-9 w-full rounded-md border border-brand-border bg-brand-page px-3 text-xs text-brand-ink" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.14em] text-brand-tertiary block mb-1.5">Volume · {vol}</label>
            <Slider value={[vol]} min={0} max={100} step={1} onValueChange={(v) => setVol(v[0])} data-testid="sonos-sched-vol" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={save} disabled={saving} data-testid="sonos-sched-save"
            className="inline-flex items-center gap-1.5 text-sm rounded-md px-4 py-2 bg-brand-ink text-brand-page hover:opacity-90 transition-opacity disabled:opacity-50">
            {saving ? "Saving…" : "Save schedule"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SchedulesBlock({ location, groups, playlists, schedules, reload }) {
  const toggleEnabled = async (s) => {
    try { await api.patch(`/sonos/schedules/${s.id}`, { ...s, enabled: !s.enabled }); reload(); }
    catch { toast.error("Could not update schedule"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete the ${s.playlist_name} schedule?`)) return;
    try { await api.delete(`/sonos/schedules/${s.id}`); toast.success("Schedule removed"); reload(); }
    catch { toast.error("Could not delete schedule"); }
  };
  return (
    <div className="mt-6 border-t border-brand-border pt-6" data-testid={`sonos-schedules-${location}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h4 className="inline-flex items-center gap-2 text-sm font-medium text-brand-ink">
          <CalendarClock className="h-4 w-4 text-brand-tertiary" /> Play schedules
        </h4>
        <ScheduleDialog location={location} groups={groups} playlists={playlists} onSaved={reload} />
      </div>
      {!schedules.length ? (
        <p className="text-xs text-brand-tertiary">No schedules yet. Add one to auto-play a playlist during open hours.</p>
      ) : (
        <div className="space-y-2">
          {schedules.map((s) => (
            <div key={s.id} data-testid={`sonos-schedule-${s.id}`}
              className="flex items-center gap-3 border border-brand-border rounded-lg px-3 py-2.5 bg-brand-page">
              <Switch checked={s.enabled} onCheckedChange={() => toggleEnabled(s)} data-testid={`sonos-schedule-toggle-${s.id}`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-brand-ink truncate">{s.playlist_name} <span className="text-brand-tertiary">in</span> {s.group_name}</p>
                <p className="text-xs text-brand-muted font-mono">
                  {s.start_time}–{s.end_time} · {s.days.map((d) => DAYS[d]).join(" ")} · vol {s.volume}
                </p>
              </div>
              <button onClick={() => remove(s)} data-testid={`sonos-schedule-del-${s.id}`}
                className="text-brand-tertiary hover:text-brand-neg transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MusicPanel({ location = null, showHeaders = true }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [connected, setConnected] = useState(false);
  const [mock, setMock] = useState(false);
  const [canConnect, setCanConnect] = useState(false);
  const [unassigned, setUnassigned] = useState([]);
  const [assignable, setAssignable] = useState([]);

  const load = useCallback(async () => {
    try {
      const params = location ? { location } : {};
      const [ov, pl, sc] = await Promise.all([
        api.get("/sonos/overview", { params }),
        api.get("/sonos/playlists"),
        api.get("/sonos/schedules", { params }),
      ]);
      setConfigured(ov.data.configured);
      setConnected(ov.data.connected);
      setMock(ov.data.mock);
      setCanConnect(ov.data.can_connect ?? (user?.role === "admin"));
      setData(ov.data.locations || []);
      setUnassigned(ov.data.unassigned || []);
      setAssignable(ov.data.assignable || []);
      setPlaylists(pl.data.playlists || []);
      setSchedules(sc.data.schedules || []);
    } catch (e) { setData([]); toast.error("Music couldn't load", { description: e.userMessage, id: "music" }); }
    finally { setLoading(false); }
  }, [location, user]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("sonos") === "connected") {
      toast.success("Sonos connected");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const reloadSchedules = useCallback(async () => {
    const params = location ? { location } : {};
    try { const { data } = await api.get("/sonos/schedules", { params }); setSchedules(data.schedules || []); } catch { /* noop */ }
  }, [location]);

  const assignRoom = async (room, loc) => {
    try {
      const { data } = await api.post(`/sonos/rooms/${room.id}/location`, { location: loc, group_name: room.name });
      setUnassigned((cur) => cur.filter((r) => r.id !== room.id));
      if (data.group) {
        setData((cur) => {
          const list = cur ? [...cur] : [];
          const idx = list.findIndex((l) => l.location === loc);
          if (idx >= 0) {
            list[idx] = { ...list[idx], groups: [...list[idx].groups.filter((g) => g.id !== data.group.id), data.group] };
          } else {
            list.push({ location: loc, groups: [data.group] });
          }
          return list.sort((a, b) => a.location.localeCompare(b.location));
        });
      }
      toast.success(`${room.name} → ${loc}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not assign room"); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect the Sonos account from the hub?")) return;
    try { await api.post("/sonos/disconnect"); toast.success("Sonos disconnected"); setConnected(false); setData([]); }
    catch { toast.error("Could not disconnect"); }
  };

  if (loading) return <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="sonos-loading">{[...Array(3)].map((_, i) => <div key={i} className="h-72 rounded-xl bg-brand-surface border border-brand-border animate-pulse" />)}</div>;
  if (!configured) return <div className="text-sm text-brand-muted" data-testid="sonos-not-configured">Sonos isn't configured yet.</div>;
  if (!connected && !mock) return <ConnectCTA canConnect={canConnect} />;

  return (
    <div className="space-y-10" data-testid="music-panel">
      {mock && (
        <div className="flex items-center gap-2 text-[11px] text-brand-gold bg-brand-gold/10 border border-brand-gold/25 rounded-lg px-3 py-2" data-testid="sonos-mock-banner">
          <Disc3 className="h-3.5 w-3.5" />
          Preview mode — live Sonos control activates once the account is connected. Controls &amp; schedules are fully functional here.
        </div>
      )}
      {connected && !location && user?.role === "admin" && (
        <div className="flex items-center justify-end">
          <button onClick={disconnect} data-testid="sonos-disconnect-btn"
            className="inline-flex items-center gap-1.5 text-xs text-brand-tertiary hover:text-brand-neg transition-colors">
            <Unlink className="h-3.5 w-3.5" /> Sonos connected · Disconnect
          </button>
        </div>
      )}
      {!location && (
        <UnassignedRooms rooms={unassigned} assignable={assignable} onAssign={assignRoom} />
      )}
      {!data?.length && !unassigned.length ? (
        <div className="text-sm text-brand-muted py-6 text-center" data-testid="sonos-empty">No speakers available{location ? " for this location" : ""}.</div>
      ) : data.map((g) => {
        const locSchedules = schedules.filter((s) => s.location === g.location);
        return (
          <section key={g.location} data-testid={`sonos-location-${g.location}`}>
            {showHeaders && <h3 className="font-serif text-xl text-brand-ink mb-4">{g.location}</h3>}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {g.groups.map((grp) => (
                <GroupCard key={grp.id} group={grp} location={g.location} playlists={playlists} />
              ))}
            </div>
            <SchedulesBlock location={g.location} groups={g.groups} playlists={playlists}
              schedules={locSchedules} reload={reloadSchedules} />
          </section>
        );
      })}
    </div>
  );
}
